// src/features/analytics/components/OverviewCards.tsx

import { Badge, Group, Paper, SimpleGrid, Skeleton, Text } from '@mantine/core';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags';
import { NO_DATA_COPY } from './StatTile';
import type { DatasetMeta, OverviewData } from '../types';

interface OverviewCardsProps {
  overview: OverviewData | null;
  isLoading: boolean;
  /**
   * Returning-viewer share of sessions (0..1) derived by the page from the
   * playback/daily viewer-mix fields (extend, don't refetch); null when no
   * day in range carries the fields. Rendered only behind the expansion flag.
   */
  returningShare?: number | null;
  /** The playback/daily query's loading state (its own card only). */
  returningShareLoading?: boolean;
  /**
   * Hide the returning-viewers card entirely: the split is a UTC-day
   * materialization the gateway omits on the local-day path (dashboard
   * ADR-009) — hidden beats an always-empty tile.
   */
  hideReturningShare?: boolean;
}

const PLAYBACK_EMPTY_COPY = 'Playback analytics begins with the new player rollout.';

function hasDatasetData(meta: DatasetMeta | undefined): boolean {
  return !!meta?.coverage && meta.freshness?.state !== 'empty';
}

interface StatCardProps {
  label: string;
  value: string | null;
  /** Shown instead of a value when the dataset is absent — never a zero. */
  emptyCopy?: string;
  secondary?: React.ReactNode;
  isLoading: boolean;
}

function StatCard({ label, value, emptyCopy = NO_DATA_COPY, secondary, isLoading }: StatCardProps) {
  return (
    <Paper withBorder p="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
        {label}
      </Text>
      {isLoading ? (
        <Skeleton height={28} mt={6} width="60%" />
      ) : value !== null ? (
        <>
          <Text size="xl" fw={600} mt={2}>
            {value}
          </Text>
          {secondary}
        </>
      ) : (
        <Text size="sm" c="dimmed" mt={6}>
          {emptyCopy}
        </Text>
      )}
    </Paper>
  );
}

/**
 * Overview stat cards. Each card honors its own dataset meta independently —
 * playback empty while CDN has data is the expected Phase 5 production state.
 */
export function OverviewCards({
  overview,
  isLoading,
  returningShare = null,
  returningShareLoading = false,
  hideReturningShare = false,
}: OverviewCardsProps) {
  const playbackReady = !!overview?.playback && hasDatasetData(overview.datasets.playbackDaily);
  const playback = playbackReady ? overview.playback : undefined;

  const transferReady = !!overview?.transfer && hasDatasetData(overview.datasets.transfer);
  const transfer = transferReady ? overview.transfer : undefined;

  const concurrency = overview?.concurrency;
  const presenceReady = !!concurrency?.hasData && hasDatasetData(overview?.datasets.presence);

  // CDN delivery exposure rides the same flag as the delivery tab/route
  // (ADR-010 §3: never show two live bandwidth methodologies at once) —
  // transfer numbers stay dark on the Overview too until the flip. The value
  // arrives bundled in the overview response; only rendering is gated.
  const showDelivery = FEATURE_FLAGS.analyticsDeliveryEnabled;
  // Returning viewers (metrics expansion) rides its own build flag the same way,
  // and is hidden on the local-day path (ADR-009: UTC-day materialization).
  const showExpansion = FEATURE_FLAGS.analyticsExpansionEnabled && !hideReturningShare;
  const xlCols = 4 + (showDelivery ? 1 : 0) + (showExpansion ? 1 : 0);

  return (
    <SimpleGrid cols={{ base: 1, xs: 2, md: 3, xl: xlCols }} spacing="sm">
      <StatCard
        label="Sessions"
        value={playback ? AnalyticsUtils.formatCount(playback.sessions) : null}
        emptyCopy={PLAYBACK_EMPTY_COPY}
        isLoading={isLoading}
      />
      <StatCard
        label="Watch time"
        value={playback ? AnalyticsUtils.formatWatchTime(playback.watchTimeMs) : null}
        emptyCopy={PLAYBACK_EMPTY_COPY}
        isLoading={isLoading}
      />
      <StatCard
        label="Unique viewers"
        value={playback ? AnalyticsUtils.formatCount(playback.uniqueViewers) : null}
        emptyCopy={PLAYBACK_EMPTY_COPY}
        isLoading={isLoading}
      />
      {showExpansion && (
        <StatCard
          label="Returning viewers"
          value={returningShare !== null ? AnalyticsUtils.formatPercent(returningShare) : null}
          secondary={
            returningShare !== null ? (
              <Text size="xs" c="dimmed" mt={2}>
                of sessions in range
              </Text>
            ) : undefined
          }
          emptyCopy="No viewer-mix data for this range yet."
          isLoading={returningShareLoading}
        />
      )}
      {showDelivery && (
        <StatCard
          label="CDN delivery"
          value={transfer ? AnalyticsUtils.formatBytes(transfer.bytesTransferred) : null}
          secondary={
            transfer ? (
              <Text size="xs" c="dimmed" mt={2}>
                {AnalyticsUtils.formatBytes(transfer.finalizedBytes)} final ·{' '}
                {AnalyticsUtils.formatBytes(transfer.provisionalBytes)} provisional
              </Text>
            ) : undefined
          }
          isLoading={isLoading}
        />
      )}
      <StatCard
        label="Concurrent viewers"
        value={
          presenceReady && concurrency?.current !== undefined
            ? AnalyticsUtils.formatCount(concurrency.current)
            : null
        }
        secondary={
          presenceReady && concurrency?.peak !== undefined ? (
            <Group gap={6} mt={2}>
              <Badge variant="light" size="sm">
                Peak {AnalyticsUtils.formatCount(concurrency.peak)}
              </Badge>
            </Group>
          ) : undefined
        }
        isLoading={isLoading}
      />
    </SimpleGrid>
  );
}
